import { IKeepAliveBaseOptions, IKeepAlive } from '@nab/x-types';

/**
 * [[include:packages/core/sdk-core/src/KeepAlive/README.md]]
 */
export abstract class BaseKeepAlive implements IKeepAlive {
    /**
     *  Overridden Properties
     */

    /**
     * Indicates the defaults that will be used to start the keep alive.
     */
    protected defaultOptions: IKeepAliveBaseOptions = {
        /**
         * The Idle Time used here is based on the Stargaze defaults for idle timeout (5 minutes).
         */
        idleTime: 300,

        /**
         * Indicates the default early check factor, that will be used to perform the check before the idle time is expired.
         */
        earlyCheckFactorForHeartbeat: 0.75,

        /**
         * Indicates the default events, that will be used to monitor users active status.
         */
        events: ['mousemove', 'mousedown', 'keypress', 'touchstart', 'touchmove']
    };

    protected heartbeatIntervalId: number;

    protected isUserActive: boolean = false;

    /**
     * Public Properties
     */

    /**
     * Exposes the boolean that will inform where the user is active or not
     */
    public get IsUserActive(): boolean {
        return this.isUserActive;
    }

    constructor(protected options: Partial<IKeepAliveBaseOptions>) {
        this.options = { ...this.defaultOptions, ...options };
    }

    protected inactivateUser(): void {
        this.isUserActive = false;
    }

    protected keepUserActive(): void {
        this.isUserActive = true;
    }

    protected startHeartBeatTimeOut(): void {
        const heartbeatIdleTimer = Math.round(this.options.idleTime * 1000 * this.options.earlyCheckFactorForHeartbeat);

        // Had to bind the this here as we are following inheritance and use of arrow functions cause reference issues.
        // But the event handler works on the container context and the this gets overridden.
        // `any` is required for this code when used server side node js returns a TimeOut type && browser returns number,
        // but still can be used as the same way to clear the timer.
        this.heartbeatIntervalId = setInterval(this.heartbeatCalls.bind(this), heartbeatIdleTimer) as any;
    }

    /**
     * Abstract methods
     */

    /**
     * Function used to define the heartbeat calls made by the timer job.
     * This method will be provided as the input to the event listener.
     */
    protected abstract heartbeatCalls(): void;

    /**
     * Function used to define the events that needs to be registered for listening and determining that the user is active.
     * The method has been extracted as the events setup are different for Web vs Server based code.
     */
    protected abstract setupEvents(): void;

    /**
     * Function used to destroy the events that were to be registered, when the keep alive functionality needs to be destroyed.
     * The method has been extracted as the events destruction are different for Web vs Server based code.
     */
    protected abstract destroyEvents(): void;

    /**
     * @function destroy
     * @description
     * Destroys the time jobs that were initialized for the heart beat function.
     * check if onUserInActive exists and is a function,
     * then call onUserInActive for feedback if the user is in-active
     */
    public destroy() {
        clearInterval(this.heartbeatIntervalId);
        this.destroyEvents();
        const { onUserInActive } = this.options;
        if (onUserInActive && typeof onUserInActive === 'function') {
            onUserInActive();
        }
    }

    /**
     * Init:
     * Initializes the keep alive timers based on the options provided
     */
    public init() {
        this.setupEvents();
        this.startHeartBeatTimeOut();
    }
}
